import React, { useState } from 'react';
import isEmpty from 'lodash/isEmpty';

// api, helpers
import { checkEmailStatus } from '/src/utilities/helpers';
import researchApi from '/src/apis/researchApi';
import AmplitudeHelperClass from '/src/utilities/amplitudeHelper';
import * as ReportHelpers from './helpers/ReportDownloadHelpers';

// constants
import { ar, en } from "/src/utilities/constants/dictionary";
import { REPORT_TYPES } from '/src/utilities/constants';



const amplitudeHelper = new AmplitudeHelperClass();

export const ResearchActions = (props) => {

    const { user, data } = props;
    const { is_loggedin } = user;
    const isPurchased = (data.is_user_purchased == 1);
    const canDownload = (data.download_policy && data.download_policy.is_allowed);
    const freeReport = (data.type_id == REPORT_TYPES.free || data.type_id == REPORT_TYPES.sponsored);
    const paidReport = (data.type_id == REPORT_TYPES.paid);
    const dictionary = (data.lang == "ar") ? ar() : en();
    const subbedUser = (data.download_policy && data.download_policy.user_sub_date !== null);
    const accessRestrictionMessage = "You don't have access to this report";
    const backendPolicyMessage = "User not allowed to download this report";

    // state variables
    const [isDownloading, setIsDownloading] = useState(false);

    const amplitudeTrack = () => {
        let userType = null;
        if (is_loggedin) {
            userType = (subbedUser ? 'Subscribed User' : 'Free User');
        } else {
            userType = 'Logged Out User';
        }

        amplitudeHelper.trackEvent('Clicked Access Report', {
            report_name: data.name,
            user_type: userType,
            clicked_from: 'Top Action Button'
        });
    }

    const handleDownload = () => {

        // stop multiple clicks when dl is in progress
        if (isDownloading) {
            return;
        }

        if (!checkEmailStatus(user.email_status)) {
            return;
        }

        /* workarounds for 'window.open' issues in Safari
         *
         * 1 -> Safari blocks window.open when made inside an async call
         * to fix this, we open a blank window before the async call & update it later
         * 
         * 2 -> when downloading multiple reports, Safari only opens 1 tab & keeps refreshing for each new report,
         * this happens in the background without the user noticing it
         * to fix this, the window should be assigned a unique id for each report
        **/
        const windowId = `downloading_report_${data.id}`; // must be unique for each report downloaded
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const isPaidReport = (data.type_id == REPORT_TYPES.paid);
        setIsDownloading(true);

        if (isSafari && !isPaidReport) {
            window.open('', windowId);
        }

        researchApi.downloadReport(data.id, user.user_id, user.email).then(
            async response => {

                if (response.success) {
                    const downloadPath = response.downloadPath;
                    try {
                        setIsDownloading(false);
                        if (isPaidReport && downloadPath) {
                            await ReportHelpers.downloadWatermarkedReport(downloadPath, user);
                        } else if (downloadPath) {
                            window.open(downloadPath, windowId);
                        } else {
                            // handle no research file uploaded
                            handleDownloadError('Research file not found');
                        }
                    } catch (error) {
                        console.log(error, 'Error while downloading report');
                        const isBackendPolicyDenied = (response?.errors?.[0]?.message === backendPolicyMessage);
                        handleDownloadError(isBackendPolicyDenied ? accessRestrictionMessage : 'Something went wrong');
                    }
                } else {
                    const isBackendPolicyDenied = (response?.errors?.[0]?.message === backendPolicyMessage);
                    const error = isBackendPolicyDenied ? accessRestrictionMessage : (response?.errors?.[0]?.message || 'Access denied to the file');
                    handleDownloadError(error);
                }

            }
        ).catch((error) => {
            const isBackendPolicyDenied = (error?.errors?.[0]?.message === backendPolicyMessage);
            const errorMessage = isBackendPolicyDenied
                ? accessRestrictionMessage
                : 'Access denied to the file';

            handleDownloadError(errorMessage);
        });

    }

    const handleDownloadError = (error, isQuotaOver) => {
        const { setParentState } = props;
        setParentState({
            downloadError: error,
            // determine if it is an error message or an information one
            modalTitle: (isQuotaOver ? '' : 'Download Error'),
            isErrorModal: (isQuotaOver ? false : true)
        });
        setIsDownloading(false);
        if (typeof window.jQuery !== 'undefined' && window.jQuery.fn?.modal) {
            $('#messagePopup').modal({
                backdrop: 'static', keyboard: true, show: true,
            });
        }
    }

    const getReportQuota = () => {
        let downloads = 0; // what user downloaded
        let remaining = 0; // quota limit
        const hasDownloadPolicy = (data && data.download_policy && !isEmpty(data.download_policy.data));

        if (hasDownloadPolicy) {
            const hasCategoryLimit = (!isEmpty(data.download_policy.data[data.category_id]));
            const hasDlPolicyData = (!isEmpty(data.download_policy.data.all));
            let limit = 0;
            let total = 0;

            if (hasCategoryLimit) {
                limit = Number(data.download_policy.data[data.category_id].limit) || 0;
                total = Number(data.download_policy.data[data.category_id].total) || 0;
            } else if (hasDlPolicyData) {
                limit = Number(data.download_policy.data.all.limit) || 0;
                total = Number(data.download_policy.data.all.total) || 0;
            }

            remaining = limit;
            downloads = Math.min(total, limit);
        }

        return ({
            downloads, remaining
        })
    }

    // render

    if (freeReport) {
        if (is_loggedin) {
            // free/sponsored report + logged in
            return (
                <a className="btn btn-main" onClick={handleDownload} target="_blank" rel="noopener noreferrer">
                    {dictionary.download}
                    {isDownloading ?
                        <i className="fas fa-circle-notch fa-spin"></i>
                        :
                        <i className="fas fa-arrow-circle-down"></i>
                    }
                </a>
            );
        } else {
            // free/sponsored report + logged out
            return (
                <a className="btn btn-main" data-toggle="modal" data-target="#researchDownloadForm">
                    {dictionary.download}
                    <i className="fas fa-arrow-circle-down"></i>
                </a>
            );
        }
    } else if (paidReport) {
        if (is_loggedin && (isPurchased || canDownload)) {
            // paid report + subscribed user with quota left
            const quota = getReportQuota();
            const quotaExhausted = quota.remaining > 0 && quota.downloads >= quota.remaining;
            return (
                <div className="content-body-widget-main">
                    <div className="db-box widget-box">
                        <a className={`btn ${quotaExhausted ? 'btn-secondary text-white disabled' : 'btn-main'}`} onClick={quotaExhausted ? undefined : handleDownload} target="_blank" rel="noopener noreferrer" style={quotaExhausted ? { pointerEvents: 'none' } : {}} aria-disabled={quotaExhausted}>
                            {dictionary.download}
                            {isDownloading ?
                                <i className="fas fa-circle-notch fa-spin"></i>
                                :
                                <i className="fas fa-arrow-circle-down"></i>
                            }
                        </a>
                        {quota.remaining > 0 &&
                            <p className="report-quota-message text-secondary">
                                You've downloaded <b>{quota.downloads}</b> of <b>{quota.remaining}</b> reports
                            </p>
                        }
                    </div>
                </div>
            );
        } else if (is_loggedin && !canDownload && subbedUser) {
            // paid report + subscribed user has no quota left
            const isQuotaOver = true;
            const quota = getReportQuota();
            const error = "You've already used your full report quota for this subscription period. Please contact your account manager if you need access to more reports";
            return (
                <div className="content-body-widget-main">
                    <div className="db-box widget-box">
                        <a className="btn btn-secondary text-white" onClick={() => handleDownloadError(error, isQuotaOver)} target="_blank" rel="noopener noreferrer">
                            {dictionary.download}
                            <i className="fas fa-arrow-circle-down"></i>
                        </a>
                        {quota.remaining > 0 &&
                            <p className="report-quota-message text-secondary">
                                You've downloaded <b>{quota.downloads}</b> of <b>{quota.remaining}</b> reports
                            </p>
                        }
                    </div>
                </div>
            );
        } else {
            // paid report + logged out || paid report + free user
            return (
                <button className="btn btn-main" data-toggle="modal" data-target="#contactFormModal" onClick={() => amplitudeTrack()}>
                    {dictionary.requestAccess}
                    <i className="fas fa-arrow-circle-down"></i>
                </button>
            );
        }
    } else {
        return null;
    }

}
